/**
 * French FAQ translations
 * Source of truth match - English structure
 * Last Updated: January 5, 2026 | Version 3.0
 */
export const faq = {
  // Page Meta
  pageTitle: "Questions Fréquentes",
  heroSubtitle: "Tout ce que Vous Devez Savoir sur l'Observatoire des Futurs de la Jeunesse",
  heroDescription: "Réponses complètes sur le fonctionnement de l'Observatoire, comment l'Indice des Futurs de la Jeunesse a été développé, et comment vous pouvez utiliser ou contribuer à cette plateforme.",
  lastUpdated: "Dernière Mise à Jour : 5 Janvier 2026",
  version: "Version 3.0",
  availableLanguages: "Disponible en Anglais, Portugais, Espagnol, Français et Japonais",

  // Section Navigation
  sectionUnderstanding: "Comprendre",
  sectionMethodology: "Méthodologie",
  sectionData: "Données et Sources",
  sectionUsing: "Utiliser l'Observatoire",
  sectionFuture: "Développement Futur",
  sectionEvolve: "Plan 2026",
  sectionCommon: "Questions Courantes",
  sectionContact: "Contact",

  // Section 1: Understanding the Observatory
  understandingTitle: "Comprendre l'Observatoire",
  
  // Q1: What is the Youth Futures Observatory?
  q1Title: "Qu'est-ce que l'Observatoire des Futurs de la Jeunesse ?",
  q1Answer: "L'Observatoire des Futurs de la Jeunesse est une plateforme d'intelligence basée sur des preuves qui combine des données vérifiées des registres institutionnels de la JCI et des recherches mondiales sur la jeunesse pour cartographier les opportunités pour les jeunes dans le monde entier. Il est conçu pour aider les Organisations Nationales et Locales de la JCI à comprendre le contexte plus large dans lequel elles opèrent et à identifier où leurs efforts de développement du leadership ont le plus grand impact.",
  q1Detail: "Plutôt que de simplement compter les membres ou les programmes de la JCI, l'Observatoire pose une question fondamentale : Comment la JCI contribue-t-elle à élargir les opportunités pour les jeunes dans le monde ?",
  q1Integrates: "Il répond à cela en intégrant :",
  q1IntegratesJCI: "Données Institutionnelles de la JCI — Membres réels, programmes, participation des membres dans 114 pays",
  q1IntegratesGlobal: "Indices Mondiaux d'Opportunité pour les Jeunes — Données comparatives sur la santé mentale, le développement de la main-d'œuvre et l'entrepreneuriat dans les mêmes 114 pays",
  q1IntegratesStrategic: "Documentation Stratégique — Objectifs de Vision 2040 et ambitions de croissance de la JCI jusqu'en 2040",
  q1Result: "Le résultat est un outil stratégique qui montre à la JCI où se développer, quoi prioriser et comment mesurer le succès dans l'avancement des opportunités mondiales pour les jeunes.",

  // Q2: Why "Youth Futures"?
  q2Title: "Pourquoi s'appelle-t-il Observatoire 'Futurs de la Jeunesse' ?",
  q2Answer: "'Futurs de la Jeunesse' reflète un concept important des études prospectives — le domaine académique qui examine comment les décisions et les actions façonnent les futurs possibles. Le nom encode trois idées clés :",
  q2Idea1Title: "Le Pluriel de 'Futurs'",
  q2Idea1: "Les jeunes n'ont pas un seul avenir prédéterminé. Ils ont de multiples futurs possibles selon les opportunités disponibles, l'accès aux ressources, la qualité de l'éducation, le soutien en santé mentale et la force de l'écosystème entrepreneurial.",
  q2Idea2Title: "La Perspective des Études Prospectives",
  q2Idea2: "Les études prospectives nous enseignent que l'avenir n'est pas prédéterminé — il est façonné par les choix que nous faisons aujourd'hui. En analysant les conditions actuelles, nous cartographions les futurs probables que les jeunes hériteront. En identifiant les lacunes, nous identifions où la JCI peut intervenir pour créer de meilleurs futurs.",
  q2Idea3Title: "Prise de Décision Basée sur les Données",
  q2Idea3: "Comprendre ces futurs nécessite des données rigoureuses sur ce qui se passe actuellement dans chaque région, afin que les dirigeants puissent prendre des décisions éclairées sur l'endroit où leurs efforts auront le plus grand impact.",
  q2Summary: "L'Observatoire donne voix à la réalité que l'opportunité pour les jeunes n'est pas distribuée uniformément à l'échelle mondiale. Un jeune en Europe fait face à des paysages d'opportunité vastement différents d'un jeune en Afrique et au Moyen-Orient.",

  // Q3: Why was it created?
  q3Title: "Pourquoi les Conseils de Développement de la JCI ont-ils créé cet Observatoire ?",
  q3Answer: "Le 4ème Objectif Stratégique du Plan Stratégique 2023-2027 de la JCI est l'Innovation Basée sur les Données. Cet objectif stipule : 'Pour être une organisation leader, nous devons être à la pointe de la technologie et de l'innovation.'",
  q3Questions: "L'Observatoire a été créé pour répondre à trois questions :",
  q3Question1: "Comment prouvons-nous notre impact ? — La JCI avait besoin d'une manière rigoureuse et vérifiable de démontrer l'impact par les données, pas seulement par des histoires.",
  q3Question2: "Où devrions-nous concentrer nos efforts ? — Avec 4 641 Organisations Locales dans 114 pays, la JCI a besoin d'intelligence stratégique sur l'endroit où les efforts ont le plus grand impact.",
  q3Question3: "Comment nous alignons-nous sur les priorités mondiales ? — En cartographiant le travail de la JCI par rapport aux indices mondiaux d'opportunité pour les jeunes, la JCI peut démontrer son alignement avec les ODD de l'ONU et se positionner comme partenaire stratégique.",
  q3Conclusion: "L'Observatoire est l'Innovation Basée sur les Données en pratique — utilisant des données vérifiées et une analyse systématique pour guider les décisions stratégiques.",

  // Characteristics
  characteristics: {
    dataBased: "Basé sur des Données Réelles",
    dataBasedDesc: "Uniquement des données vérifiables et auditables",
    transparent: "Transparence Totale",
    transparentDesc: "Méthodologie entièrement exposée",
    collaborative: "Collaboratif",
    collaborativeDesc: "Construit avec les contributions de toute la communauté JCI",
    accessible: "Accessible",
    accessibleDesc: "Gratuit et ouvert pour usage non commercial",
    scientific: "Scientifique",
    scientificDesc: "Aligné sur les normes internationales de recherche",
    inclusive: "Inclusif",
    inclusiveDesc: "Reflète la diversité mondiale de la JCI",
  },

  // Section 2: Observatory Methodology
  methodologyTitle: "Méthodologie de l'Observatoire",

  // Q4: How was the YFI developed?
  q4Title: "Comment l'Indice des Futurs de la Jeunesse a-t-il été développé ?",
  q4Answer: "L'Indice des Futurs de la Jeunesse (YFI) n'est pas une nouvelle invention créée pour la JCI. C'est une synthèse et une adaptation de méthodologies de recherche mondiale établies qui mesurent l'opportunité pour les jeunes.",
  q4Steps: "Voici comment il a été développé :",
  q4Step1Title: "Étape 1 : Identification des Cadres Existants",
  q4Step1: "Les équipes de recherche ont examiné les indices d'opportunité pour les jeunes existants provenant d'institutions mondiales : Atlas de Santé Mentale de l'OMS, bases de données d'emploi de l'OIT, statistiques d'éducation de l'UNESCO, études du Global Entrepreneurship Monitor.",
  q4Step2Title: "Étape 2 : Conception de la Structure du YFI",
  q4Step2: "Ces dimensions ont été intégrées dans un indice composite unique utilisant une pondération basée sur la recherche :",
  q4Weight1: "Santé Mentale : 40% (accès aux services, taux de prévalence, mortalité par suicide)",
  q4Weight2: "Main-d'œuvre et Éducation : 30% (chômage des jeunes, taux NEET, développement des compétences)",
  q4Weight3: "Entrepreneuriat : 30% (environnement des affaires, accès au financement, capacité d'innovation)",
  q4WeightRationale: "La pondération reflète la recherche montrant que l'opportunité pour les jeunes dépend le plus fortement de la santé mentale (qui permet toutes les autres capacités), suivie de la préparation au travail, puis de la capacité entrepreneuriale.",
  q4Step3Title: "Étape 3 : Application aux 114 Pays de la JCI",
  q4Step3: "Le cadre du YFI a été calculé pour les 114 pays où la JCI opère, créant une carte mondiale d'opportunité pour les jeunes qui s'aligne directement sur l'empreinte géographique de la JCI.",
  q4Step4Title: "Étape 4 : Validation des Résultats",
  q4Step4: "Les résultats ont été vérifiés par recoupement avec les retours des membres de la JCI des enquêtes de 2025 et la recherche mondiale publiée sur le développement des jeunes.",
  q4Result: "Le Résultat : Un indice rigoureux et auditable ancré dans la recherche établie, pas dans la spéculation ou les modèles.",

  // YFI Formula
  yfiFormulaTitle: "Formule du YFI",
  yfiFormula: "YFI = (0,40 × SM) + (0,30 × MO) + (0,30 × ENT)",
  yfiMH: "SM = Composante de Santé Mentale (poids de 40%)",
  yfiWF: "MO = Composante de Main-d'œuvre et Éducation (poids de 30%)",
  yfiENT: "ENT = Composante d'Entrepreneuriat (poids de 30%)",

  // Q5: Dashcity Methodology
  q5Title: "Comment fonctionne la Méthodologie en 5 Phases Dashcity ?",
  q5Answer: "La Méthodologie en 5 Phases Dashcity fournit la structure stratégique de la façon dont l'Observatoire a été construit et comment il évoluera. Chaque phase a un objectif spécifique :",
  
  phase1: "IDENTIFIER",
  phase1Status: "Terminé",
  phase1Dates: "Sep-Nov 2025",
  phase1Objective: "Cartographier l'écosystème complet du leadership des jeunes",
  phase1Activities: "Analysé le Plan Stratégique de la JCI 2023-2027 ; Diagnostiqué le paysage institutionnel (114 pays, 4 641 organisations locales, 148 122 membres) ; Identifié la lacune : les données de la JCI existaient en silos sans analyse unifiée",
  phase1Deliverable: "Cadre stratégique et portée de la recherche",

  phase2: "MESURER",
  phase2Status: "Terminé",
  phase2Dates: "Oct-Déc 2025",
  phase2Objective: "Établir des normes de données rigoureuses et des processus de validation",
  phase2Activities: "Établi 7 critères de qualité non négociables ; Vérifié toutes les sources de données primaires (Rapport d'Impact de la JCI, enquêtes, Vision 2040) ; Intégré les sources de données mondiales (OMS, OIT, UNESCO, Banque Mondiale, GEM) ; Mis en œuvre un processus de validation en 5 étapes",
  phase2Deliverable: "Architecture de données et normes de qualité",

  phase3: "PROMOUVOIR",
  phase3Status: "En Cours",
  phase3Dates: "Déc 2025-Jan 2026",
  phase3Objective: "Communiquer la valeur et engager les parties prenantes",
  phase3Activities: "Développé des canaux d'engagement des parties prenantes ; Construit des directives de contribution et des structures d'incitation ; Produit des ressources multilingues (EN, PT, ES, FR, JA)",
  phase3Deliverable: "Kit de communication et infrastructure d'engagement",

  phase4: "DIRIGER",
  phase4Status: "En Cours",
  phase4Dates: "Jan-Fév 2026",
  phase4Objective: "Établir la gouvernance et la supervision de la qualité",
  phase4Activities: "Documenté les processus de validation (porte de qualité en 7 étapes) ; Défini les cycles de mise à jour (annuel, intermédiaire, continu) ; Établi des boucles de rétroaction (Communauté → Équipes → Conseils → Conseil d'Administration)",
  phase4Deliverable: "Cadre de gouvernance et processus opérationnels",

  phase5: "METTRE À L'ÉCHELLE",
  phase5Status: "Planifié",
  phase5Dates: "Jan 2026 - Déc 2027+",
  phase5Objective: "Étendre la portée, l'impact et l'influence mondiale",
  phase5Activities: "Création de matériaux de communication pour différents publics ; Mise à l'échelle à 15+ Organisations Nationales contribuant activement ; Croissance à 1 000+ utilisateurs enregistrés ; Lancement d'APIs publiques pour les chercheurs académiques",
  phase5Deliverable: "Autorité mondiale sur le développement du leadership des jeunes",

  phaseWhyTitle: "Pourquoi Cette Approche Fonctionne",
  phaseWhy: "Chaque phase se construit sur la précédente. IDENTIFIER établit ce que nous construisons. MESURER assure la rigueur. PROMOUVOIR engage les parties prenantes. DIRIGER assure la gouvernance. METTRE À L'ÉCHELLE assure une croissance durable.",

  // Q6: Areas of Opportunity
  q6Title: "Quels sont les 4 Domaines d'Opportunité de la JCI ?",
  areaIndividual: "Développement Individuel",
  areaIndividualDesc: "Les jeunes développent des compétences essentielles de leadership, la confiance et la croissance personnelle à travers la formation, le mentorat et l'expérience pratique.",
  areaIndividualExamples: "Exemples : Prise de parole en public, débat, cours de leadership, certification des compétences",
  
  areaCommunity: "Impact Communautaire",
  areaCommunityDesc: "Les jeunes leaders identifient les besoins de la communauté et conçoivent des solutions qui bénéficient à la société.",
  areaCommunityExamples: "Exemples : Initiatives de santé, programmes éducatifs, secours en cas de catastrophe, projets environnementaux",
  
  areaInternational: "Coopération Internationale",
  areaInternationalDesc: "Les jeunes leaders construisent des réseaux mondiaux, comprennent des perspectives diverses et collaborent au-delà des frontières.",
  areaInternationalExamples: "Exemples : Partenariats de jumelage JCI, prix TOYP, événements internationaux, plaidoyer pour les droits humains",
  
  areaBusiness: "Affaires et Entrepreneuriat",
  areaBusinessDesc: "Les jeunes leaders lancent des entreprises, stimulent l'innovation et créent des opportunités économiques.",
  areaBusinessExamples: "Exemples : Jeune Entrepreneur Créatif, JCI dans les Affaires, accélérateurs de startups",

  // Q7: Connection between Areas and Observatory
  q7Title: "Quel est le lien entre les 4 Domaines et l'Observatoire ?",
  q7Answer: "L'Observatoire mesure le contexte externe dans lequel ces 4 Domaines opèrent.",
  q7Detail: "Le YFI suit : Santé Mentale (affecte les 4 Domaines — des esprits sains apprennent mieux, construisent mieux), Main-d'œuvre et Éducation (soutient les domaines Développement Individuel + Affaires), Entrepreneuriat (soutient le domaine Affaires).",
  q7Example: "Quand l'Observatoire montre que l'Afrique et le Moyen-Orient a un score YFI de 53 (vs. Europe à 75), il dit : 'Les jeunes de ces régions font face à des lacunes en santé mentale, emploi et opportunités d'affaires. C'est là que les 4 Domaines d'Opportunité de la JCI peuvent avoir un impact maximal.'",
  q7Summary: "Les 4 Domaines sont comment la JCI développe les leaders. L'Observatoire montre où et pourquoi cela compte le plus.",

  // Q8: Data-Driven Innovation
  q8Title: "Qu'est-ce que l'Innovation Basée sur les Données (le 4ème Objectif Stratégique) ?",
  q8Answer: "Le 4ème Objectif Stratégique aborde des questions critiques : Comment savons-nous si notre développement du leadership fonctionne vraiment ? Comment nous améliorons-nous ? Où devrions-nous nous concentrer ?",
  q8Means: "L'Innovation Basée sur les Données signifie :",
  q8Means1: "Mesurer ce qui compte — Utiliser des données rigoureuses sur l'opportunité pour les jeunes, l'adhésion à la JCI et l'impact",
  q8Means2: "Analyser pour obtenir des insights — Trouver des modèles (ex : 'l'accès à la santé mentale varie de 22 points entre les régions')",
  q8Means3: "Prendre des décisions éclairées — Allouer les ressources sur la base de preuves",
  q8Means4: "Amélioration continue — Recueillir des retours, ajuster la stratégie, mesurer les résultats",
  q8Conclusion: "L'Observatoire transforme la JCI d'une organisation qui sent qu'elle fait la différence en une qui peut prouver et mesurer son impact en utilisant des données vérifiées et auditables.",

  // Section 3: Data and Sources
  dataSourcesTitle: "Données et Sources",

  // Q9: What data sources?
  q9Title: "Quelles sources de données l'Observatoire utilise-t-il ?",
  primaryDataTitle: "Données Primaires de la JCI",
  primaryData1: "Rapport d'Impact de la JCI 2025 — 147 670 membres, 4 641 organisations locales, 1 000+ projets annuels, 21M+ bénéficiaires",
  primaryData2: "Vision 2040 de la JCI — Feuille de route stratégique, objectifs de croissance, plan d'innovation technologique",
  primaryData3: "Enquête sur l'Expérience des Membres 2025 — 3 767 répondants dans les 4 régions",
  
  secondaryDataTitle: "Données Mondiales Secondaires",
  secondaryData1: "Atlas de Santé Mentale de l'OMS 2024 — Couverture des services de santé mentale, estimations de prévalence",
  secondaryData2: "Base de Données de Mortalité de l'OMS — Taux de mortalité par suicide par pays",
  secondaryData3: "État Mental du Monde Sapien Labs 2024 — Recherche sur le bien-être subjectif",
  secondaryData4: "Tendances Mondiales de l'Emploi de l'OIT 2024-25 — Chômage des jeunes, statistiques NEET",
  secondaryData5: "Institut de Statistique de l'UNESCO — Inscriptions éducatives, taux d'alphabétisation",
  secondaryData6: "Banque Mondiale — Indice de Capital Humain, Facilité de Faire des Affaires",
  secondaryData7: "GEM Global Entrepreneurship Monitor 2024-25 — Activité entrepreneuriale",
  secondaryData8: "Tableau de Bord du Développement Digital de l'UIT — Infrastructure et accès numériques",
  
  dataTransparency: "Toutes les sources sont publiques et vérifiées. L'Observatoire ne contient aucune donnée propriétaire, confidentielle ou restreinte.",

  // Q10: Data quality
  q10Title: "Comment la qualité des données est-elle assurée ?",
  q10Answer: "Nous avons mis en œuvre 7 critères de qualité non négociables :",
  qualityCriteria1: "Vérifiable — Identification claire de la source (ex : 'Atlas de Santé Mentale de l'OMS 2024, page 12')",
  qualityCriteria2: "Auditable — Méthodologie complète documentée",
  qualityCriteria3: "Traçable — Origine identifiée, pas d'anonymat",
  qualityCriteria4: "Précis — Validé avant présentation",
  qualityCriteria5: "Actuel — Date claire et pertinence temporelle",
  qualityCriteria6: "Contextualisé — Limitations clairement expliquées",
  qualityCriteria7: "Cité — Crédit complet aux sources",

  validationProcessTitle: "Processus de Validation en 5 Étapes",
  validationStep1: "Soumission (1-2 jours)",
  validationStep1Desc: "Réception des données et vérification préliminaire de complétude",
  validationStep2: "Validation (5-7 jours)",
  validationStep2Desc: "Vérification technique de la méthodologie et des calculs",
  validationStep3: "Audit (5-7 jours)",
  validationStep3Desc: "Audit indépendant de l'exactitude des données",
  validationStep4: "Revue par les Pairs (5-7 jours)",
  validationStep4Desc: "Revue par des experts en la matière",
  validationStep5: "Publication (2-3 jours)",
  validationStep5Desc: "Certification finale et documentation de transparence",

  // Q11: Zero synthetic data
  q11Title: "Que signifie 'zéro données synthétiques' ?",
  q11Answer: "Zéro données synthétiques signifie : Chaque nombre que vous voyez remonte à une source de données réelle. Aucun nombre n'est inventé, estimé ou généré par des modèles.",
  
  notIncludedTitle: "Nous N'incluons PAS",
  notIncluded1: "Des projections présentées comme des faits actuels",
  notIncluded2: "Des statistiques générées par l'IA",
  notIncluded3: "Des valeurs interpolées comblant des lacunes de données",
  notIncluded4: "Des estimations basées sur des modèles (sauf si explicitement étiquetées)",
  notIncluded5: "Des distributions supposées",
  
  includedTitle: "Nous Incluons",
  included1: "Des comptages réels (ex : '147 670 membres')",
  included2: "Des statistiques basées sur des enquêtes (ex : '3 767 répondants')",
  included3: "Des indices publiés (ex : 'score YFI : 75,28')",
  included4: "Des scénarios explicitement marqués (ex : 'Vision 2040 : 500 000 membres' — clairement étiqueté comme objectif stratégique)",
  
  zeroSyntheticWhy: "Pourquoi c'est important : Vous pouvez faire confiance à ces chiffres. Ils ne sont pas générés par des algorithmes ni estimés par des consultants. Ce sont des faits vérifiés provenant de sources auditables.",

  // Section 4: Using the Observatory
  usingTitle: "Utiliser l'Observatoire",

  // Q12: Update cycles
  q12Title: "À quelle fréquence les données sont-elles mises à jour ?",
  annualUpdatesTitle: "Mises à Jour Annuelles (Complètes)",
  annualUpdates1: "Novembre-Décembre : Collecte de données de toutes les sources",
  annualUpdates2: "Janvier-Février : Validation et analyse comparative",
  annualUpdates3: "Mars : Publication de la nouvelle édition",
  annualUpdates4: "Avril-Mai : Retours et engagement des parties prenantes",
  
  intermediateUpdatesTitle: "Mises à Jour Intermédiaires (Selon Besoin)",
  intermediateUpdates1: "Nouvelles découvertes de recherche significatives",
  intermediateUpdates2: "Changements organisationnels majeurs ou innovations de programme",
  intermediateUpdates3: "Développements importants de politiques",
  intermediateUpdates4: "Corrections d'erreurs ou clarifications",
  
  continuousUpdatesTitle: "Mises à Jour Continues (En Cours)",
  continuousUpdates1: "Mises à jour de liens et corrections de références",
  continuousUpdates2: "Améliorations techniques de la plateforme",
  continuousUpdates3: "Améliorations de l'interface utilisateur",
  continuousUpdates4: "Améliorations d'accessibilité",
  
  updateGoal: "L'objectif est de maintenir l'Observatoire à jour et précis sans submerger les utilisateurs de micro-mises à jour constantes.",

  // Q13: Using the data
  q13Title: "Puis-je utiliser ces données pour ma recherche ou organisation ?",
  q13Answer: "Oui, absolument. L'Observatoire est conçu pour :",
  
  useNationalTitle: "Organisations Nationales de la JCI",
  useNational1: "Comparer votre progression par rapport aux données mondiales",
  useNational2: "Identifier où vos efforts régionaux de leadership ont le plus grand impact",
  useNational3: "Prendre des décisions stratégiques basées sur les données sur où se développer",
  
  useLocalTitle: "Organisations Locales de la JCI",
  useLocal1: "Comprendre le paysage d'opportunité pour les jeunes dans votre ville/région",
  useLocal2: "Connecter vos projets locaux aux défis mondiaux d'opportunité",
  useLocal3: "Suivre si votre travail aborde les lacunes prioritaires",
  
  useAcademicTitle: "Chercheurs Académiques",
  useAcademic1: "Accès à des données vérifiées et à une méthodologie publiée",
  useAcademic2: "APIs pour accès programmatique (prévu pour 2026)",
  useAcademic3: "Permission de citer et de s'appuyer sur les découvertes de l'Observatoire",
  
  usePolicyTitle: "Décideurs Politiques et Gouvernement",
  usePolicy1: "Preuves sur l'opportunité pour les jeunes dans votre pays",
  usePolicy2: "Comparaison avec les régions voisines",
  usePolicy3: "Intelligence stratégique pour les politiques d'éducation, d'emploi, de santé mentale",
  
  useCorporateTitle: "Partenaires Corporatifs",
  useCorporate1: "Comprendre les besoins de développement des jeunes dans vos marchés",
  useCorporate2: "Identifier où les partenariats avec la JCI pourraient avoir le plus grand impact",
  useCorporate3: "Accéder à des données vérifiées pour les rapports de responsabilité d'entreprise",
  
  publicAPIs: "APIs Publiques — Lancement au 2ème trimestre 2026 pour chercheurs et développeurs",

  // Q14: Reproducibility
  q14Title: "Les chercheurs peuvent-ils reproduire la méthodologie ?",
  q14Answer: "Oui, complètement. Tous les composants sont documentés et publiquement disponibles :",
  q14Formula: "Formule du YFI (Publiée) : YFI = (0,40 × SM) + (0,30 × MO) + (0,30 × ENT)",
  q14Sources: "Données Sources (Toutes Publiques) : Atlas de Santé Mentale de l'OMS, Base ILOSTAT de l'OIT, statistiques UNESCO, indicateurs Banque Mondiale, Moniteur GEM, Rapport d'Impact JCI 2025",
  q14Methodology: "Documentation Complète de la Méthodologie — Publiée intégralement sur la page de Méthodologie",
  q14Commitment: "Tout chercheur ayant accès à ces mêmes sources peut répliquer l'analyse, vérifier nos calculs et tester nos conclusions. C'est l'engagement envers la transparence.",

  // Section 5: Future Development
  futureTitle: "Développement Futur",

  // Q15: Future roadmap
  q15Title: "Quel est l'avenir de l'Observatoire ?",
  q15Answer: "L'Observatoire évolue à travers un plan d'expansion par phases :",
  
  roadmap2026Title: "2026 (BETA — Expansion)",
  roadmap2026Items: "Mise à l'échelle à 15+ Organisations Nationales comme contributeurs actifs ; Croissance de la communauté d'utilisateurs à 200+ membres ; Lancement d'APIs publiques pour chercheurs académiques ; Développement d'un tableau de bord d'analyse avancée ; Début de la documentation de l'influence sur les politiques",
  roadmap2026Target: "Objectifs : 200+ utilisateurs, 10+ articles médias annuels citant l'Observatoire",
  
  roadmap2027Title: "2027+ (ÉCHELLE — Autorité Mondiale)",
  roadmap2027Items: "Positionnement comme référence mondiale pour le développement du leadership des jeunes ; Intégration avec les plateformes ONU (ECOSOC), WEF, UNESCO ; Influence visible sur les politiques dans 10+ pays ; Communauté mondiale de 1 000+ utilisateurs engagés ; Publication annuelle reconnue dans les principaux médias",
  roadmap2027Target: "Objectifs : 500+ articles médias annuels, influence sur les politiques dans 10+ pays",
  
  innovationsTitle: "Innovations Stratégiques Planifiées",
  innovation2026: "2026 : Répertoire de Mentors, Pages de Partenariats, Compteur de Plaidoyer en Temps Réel",
  innovation2027: "2027+ : Newsletter Mensuelle de Recherche, Série de Webinaires avec Experts, Outils Interactifs d'Évaluation des Compétences, Calculateur de Correspondance de Subventions",

  // Q16: Contributing
  q16Title: "Comment puis-je contribuer à l'Observatoire ?",
  
  contributeNationalTitle: "Les Organisations Nationales peuvent contribuer",
  contributeNational: "Analyses et insights régionaux ; Études de cas de programmes réussis ; Données d'événements et programmes nationaux ; Retours sur comment les découvertes s'appliquent dans votre contexte",
  
  contributeLocalTitle: "Les Organisations Locales peuvent contribuer",
  contributeLocal: "Résultats de projets et métriques d'impact ; Histoires de développement des membres ; Données sur la portée communautaire et les bénéficiaires ; Leçons apprises des initiatives locales",
  
  contributeMemberTitle: "Les Membres Individuels peuvent contribuer",
  contributeMember: "Retours sur les compétences apprises à travers les programmes de la JCI ; Histoires de comment la JCI a changé votre trajectoire de leadership ; Données sur les résultats d'emploi ; Suggestions d'améliorations",
  
  contributeHowTitle: "Comment contribuer",
  contributeHow1: "Visitez la page de Contribution",
  contributeHow2: "Examinez les directives de contribution",
  contributeHow3: "Complétez le formulaire de contribution avec la documentation requise",
  contributeHow4: "Recevez des retours dans les 5-7 jours",
  contributeHow5: "Soyez reconnu comme contributeur lors de la publication",
  
  contributeIncentivesTitle: "Incitations pour les contributeurs",
  contributeIncentive1: "Citation formelle dans toutes les publications de l'Observatoire",
  contributeIncentive2: "Certificat de collaboration numérique",
  contributeIncentive3: "Reconnaissance sur les réseaux sociaux",
  contributeIncentive4: "Valide pour le CV académique",
  contributeIncentive5: "Accès aux insights de l'Observatoire avant le lancement public",

  // Q17: Vision 2040 alignment
  q17Title: "Comment cela s'aligne-t-il avec la Vision 2040 de la JCI ?",
  q17Goal: "Objectif Vision 2040 : Autonomiser 500 000 jeunes leaders d'ici 2040",
  q17Contribution: "Contribution de l'Observatoire : Intelligence stratégique pour atteindre cet objectif",
  
  vision2040PillarTitle: "Pilier Vision 2040",
  vision2040RoleTitle: "Rôle de l'Observatoire",
  
  vision2040Pillar1: "Excellence en Développement du Leadership",
  vision2040Role1: "Cartographie les lacunes de compétences (communication #1, pensée critique #2) ; identifie les priorités de formation",
  
  vision2040Pillar2: "Technologie et Innovation Numérique",
  vision2040Role2: "Suit l'adoption numérique (jci.cc : 13 351 utilisateurs, 260K abonnés sociaux) ; guide la feuille de route technologique",
  
  vision2040Pillar3: "Équité et Inclusion",
  vision2040Role3: "Révèle l'inégalité régionale d'opportunité (écart de 22 points du YFI) ; identifie où l'impact est le plus nécessaire",
  
  vision2040Pillar4: "Durabilité et Climat",
  vision2040Role4: "Fournit un contexte d'opportunité pour les jeunes pour l'alignement des politiques environnementales",
  
  vision2040QuestionsTitle: "Questions Stratégiques Auxquelles l'Observatoire Répond",
  vision2040Q1: "Où se développer ? — Afrique et Moyen-Orient (YFI le plus bas = plus grande opportunité d'impact)",
  vision2040Q2: "Quoi prioriser ? — Santé mentale (40% du poids du YFI = plus grand différenciateur)",
  vision2040Q3: "Comment mettre à l'échelle ? — Technologie (objectif d'adoption de 80%+ d'ici 2040)",
  vision2040Q4: "Mesures de succès ? — Améliorer le YFI mondial de 4-7 points ; combler l'écart Afrique et Moyen-Orient de 50%",
  
  vision2040Summary: "L'Observatoire transforme la Vision 2040 d'une aspiration en une stratégie basée sur des preuves.",

  // Section 6: 2026 Plan of Action (EVOLVE)
  evolveTitle: "Plan d'Action 2026 : EVOLVE",

  // Q21: EVOLVE connection
  q21Title: "Comment l'Observatoire est-il lié au Plan d'Action 2026 ?",
  q21Answer: "Le Plan d'Action 2026 établit trois principes fondamentaux : LEAD | CONNECT | EVOLVE. L'Observatoire est une manifestation directe du principe EVOLVE.",
  q21Quote: "'Nous devons DIRIGER avec l'attention que notre cause mérite, nous CONNECTER avec le changement que nous voulons créer, et ÉVOLUER avec le monde en mouvement rapide autour de nous.'",
  
  evolveHow: "Comment l'Observatoire Permet EVOLVE :",
  
  evolve1Title: "Évoluer à Travers la Prise de Décision Basée sur les Données",
  evolve1: "L'Observatoire fournit des preuves montrant quelles compétences sont les plus nécessaires globalement, où le développement du leadership a le plus grand impact, et comment mesurer le succès à travers les indicateurs d'opportunité pour les jeunes.",
  evolve1Summary: "Sans données, nous ne pouvons pas évoluer stratégiquement — nous ne pouvons que réagir. L'Observatoire permet une évolution proactive basée sur des preuves.",
  
  evolve2Title: "Soutenir les 4 Piliers d'Action",
  actionPillar1: "Durabilité d'Abord",
  actionPillar1Desc: "Suit la santé mentale (40% du poids du YFI) et l'opportunité à long terme pour les jeunes, assurant que le travail crée des futurs durables",
  actionPillar2: "Construire le Leadership Mondial",
  actionPillar2Desc: "Cartographie où le développement du leadership est le plus nécessaire ; valide que les programmes de la JCI développent les compétences que les jeunes priorisent",
  actionPillar3: "Affaires à Travers le Monde",
  actionPillar3Desc: "Fournit des données sur l'écosystème entrepreneurial (30% du poids du YFI) ; identifie où les programmes d'Affaires de la JCI ont le plus grand impact",
  actionPillar4: "Connecter les Vies et les Cultures",
  actionPillar4Desc: "Montre les différences régionales en opportunité pour les jeunes (écart de 22 points du YFI), démontrant pourquoi la connexion interculturelle compte",
  
  evolve3Title: "Mesurer les KPIs qui Comptent",
  evolve3: "L'Observatoire rend les KPIs significatifs en les connectant aux données externes d'opportunité pour les jeunes. Par exemple, si nous voyons une croissance de 30% de l'adhésion en Afrique et Moyen-Orient, l'Observatoire montre que cela compte parce que cette région a le YFI le plus bas (53) et le plus grand écart d'opportunité.",
  
  evolve4Title: "'Retour aux Fondamentaux' Rencontre l'Innovation Stratégique",
  evolve4: "L'Observatoire complète la Phase 1 'Retour aux Fondamentaux' du Plan 2026 en fournissant un contexte stratégique pour les programmes fondamentaux, en offrant des données de benchmarking pour la certification des formateurs, et en soutenant Mentor-Mentee 2.0 avec des preuves.",
  
  evolve5Title: "Événements Mondiaux avec un But",
  evolve5: "L'Observatoire fournit des données qui font des événements mondiaux (Sommet du Leadership des Jeunes, Semaine de la Durabilité, Forums de Leadership Féminin) des événements basés sur des preuves plutôt que cérémoniels.",
  
  evolveKeyConnection: "Connexion Clé : EVOLVE signifie s'adapter au monde en mouvement rapide. L'Observatoire est la façon dont la JCI évolue — du leadership basé sur l'intuition au leadership basé sur les données, de 'nous pensons faire la différence' à 'nous pouvons prouver notre impact.'",
  
  evolveSummary: "Le Plan d'Action 2026 définit la direction (LEAD | CONNECT | EVOLVE). L'Observatoire fournit l'intelligence qui rend l'évolution stratégique, mesurable et auditable.",

  // Section 7: Common Questions
  commonTitle: "Questions Courantes",

  // Q22: Privacy
  q22Title: "Comment l'Observatoire aborde-t-il la confidentialité et la vie privée ?",
  
  privacyMemberTitle: "Confidentialité des Membres",
  privacyMember1: "Aucune donnée individuelle de membre n'est publiée",
  privacyMember2: "Toutes les données d'enquête sont agrégées et anonymisées",
  privacyMember3: "Les répondants ne peuvent pas être identifiés dans les résultats",
  privacyMember4: "Les informations personnelles sont protégées selon la politique de confidentialité",
  
  privacyOrgTitle: "Données Organisationnelles",
  privacyOrg: "Seules les données agrégées sont publiées (ex : '147 670 membres')",
  
  privacySecurityTitle: "Sécurité des Données",
  privacySecurity1: "Transmission et stockage chiffrés",
  privacySecurity2: "Audits de sécurité réguliers",
  privacySecurity3: "Accès limité aux données brutes (uniquement l'Équipe des Opérations)",
  privacySecurity4: "Conformité au RGPD et aux lois régionales sur la confidentialité",

  // Q23: Disagreement
  q23Title: "Que faire si je ne suis pas d'accord avec les découvertes de l'Observatoire ?",
  q23Answer: "Vos retours sont essentiels. Le désaccord nous aide à nous améliorer.",
  q23Process: "Voici comment :",
  q23Step1: "Soumettez des retours détaillés à tmariano@jci.cc",
  q23Step2: "Spécifiez votre préoccupation (méthodologie, exactitude des données, interprétation)",
  q23Step3: "Vos retours vont au Conseil de Développement concerné pour examen",
  q23Step4: "Dans les 2 semaines, nous expliquerons notre méthodologie ou reconnaîtrons votre point valide",
  q23Step5: "Si vos retours révèlent une erreur, nous la corrigeons et vous citons",
  q23Summary: "L'Observatoire évolue à travers le désaccord constructif. Nous n'essayons pas d'avoir raison — nous essayons d'être précis.",

  // Q24: Staying updated
  q24Title: "Comment puis-je rester informé des développements de l'Observatoire ?",
  
  newsletterTitle: "Newsletter (Lancement 2ème trimestre 2026)",
  newsletter1: "Points forts mensuels des nouvelles découvertes",
  newsletter2: "Mise en avant des contributeurs",
  newsletter3: "Innovations à venir",
  newsletter4: "Inscrivez-vous sur la page d'Authentification",
  
  webinarsTitle: "Webinaires (Lancement 3ème trimestre 2026)",
  webinars1: "Explications d'experts sur les dimensions de l'Observatoire",
  webinars2: "Comment utiliser les données de l'Observatoire pour la planification stratégique",
  webinars3: "Questions et réponses avec les Conseils de Développement",
  webinars4: "Gratuit, ouvert à tous, multilingue",
  
  directEmailTitle: "Email Direct",
  directEmail: "tmariano@jci.cc — Questions générales, questions sur des données ou méthodologies spécifiques",

  // Contact Section
  contactTitle: "Contact et Ressources",
  
  contactTechnicalTitle: "Pour les Questions Techniques",
  contactTechnical: "tmariano@jci.cc",
  
  contactRegionalTitle: "Pour les Questions Régionales",
  contactAmericas: "Amérique : adc@jci.cc",
  contactEurope: "Europe : edc@jci.cc",
  contactAfrica: "Afrique et Moyen-Orient : amdec@jci.cc",
  contactAsia: "Asie et Pacifique : apdc@jci.cc",
  
  keyResourcesTitle: "Ressources Clés",
  keyResource1: "Méthodologie Complète",
  keyResource2: "Tableau de Bord des Données",
  keyResource3: "Comment Contribuer",
  
  accessibilityTitle: "Accessibilité",
  accessibility1: "Disponible en Anglais, Portugais, Espagnol, Français et Japonais",
  accessibility2: "PDFs téléchargeables de tout le contenu (bientôt)",
  accessibility3: "Compatible avec les lecteurs d'écran",
  accessibility4: "Langues supplémentaires sur demande",

  // CTA
  ctaTitle: "Prêt à Explorer ?",
  ctaDescription: "L'Observatoire des Futurs de la Jeunesse est construit sur des preuves. Notre engagement est envers la transparence, la rigueur et l'amélioration continue en soutien de la mission de la JCI de développer des leaders pour un monde en changement.",
  ctaExplore: "Explorer l'Observatoire",
  ctaContribute: "Contribuer à l'Observatoire",
  ctaContact: "Nous Contacter",

  // Source
  sourceAttribution: "Source : Plan Stratégique JCI 2023-2027, Méthodologie DASHCity, Rapport d'Impact JCI 2025",
};